import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { OptionalBenefitComputationFn } from '../EstimateDetailsAndOptions';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { useCallback, useMemo } from 'react';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { useGetFileUrl } from '@/hooks/useFile';
import { useEstimatePrices } from '@/hooks/useEstimatePrices';
import { useEstimateCarRental } from '@/hooks/useEstimateCarRental';
import EstimateDayStepCardTemplate from './EstimateDayStepCardTemplate';
import { CarRentalElementType } from './EstimateCarRentalElement';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useConvertPriceOnlyIfDateISProvided } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';

interface EstimateDayStepCarRentalSummaryCardProps {
  step: CircuitDayFormData['steps'][number]['transport'];
  selectedOptions: Record<string, SelectedEstimateOption>;
  date: Date;
  dayIndex: number;
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  language?: SupportedLocale;
}

const EstimateDayStepCarRentalSummaryCard = ({
  step,
  date,
  dayIndex,
  selectedOptions,
  computeOptionalBenefitPrice,
  currency = 'EUR',
  creationDate,
  accessToken,
  language,
}: EstimateDayStepCarRentalSummaryCardProps) => {
  const { stepDates, computeBenefitPriceDelta } = useEstimatePrices({
    step,
    computeOptionalBenefitPrice,
    date,
    dayIndex,
  });

  const carRentalCategories = useMemo(() => {
    const categories = step.standardBenefit?.carRental?.carCategories ?? [];
    const tLang =
      language && step.standardBenefit?.translations
        ? step.standardBenefit.translations[language]
        : undefined;
    const translatedCategories = tLang?.carRental?.carCategories ?? [];

    return categories
      .map((category, categoryIndex) => {
        if (!step.standardBenefit) return null;
        const benefitWithOnlyThisCategory = {
          ...step.standardBenefit,
          carRental: {
            carCategories: [category],
          },
        };
        const deltaPrice = computeBenefitPriceDelta(
          benefitWithOnlyThisCategory,
        );
        const translatedCategory = translatedCategories[categoryIndex];
        return {
          deltaPrice,
          category: {
            ...category,
            translations:
              language && translatedCategory
                ? { [language]: translatedCategory }
                : undefined,
          },
        };
      })
      .filter((v) => v != null);
  }, [computeBenefitPriceDelta, step, language]);

  const carRentalStandardCategory = useMemo(
    () =>
      carRentalCategories.find(({ deltaPrice }) => deltaPrice === 0)?.category,
    [carRentalCategories],
  );

  const selectedCarCategory = useMemo(
    () =>
      selectedOptions[step.id]
        ? carRentalCategories.find(
            ({ category: { id } }) =>
              selectedOptions[step.id].carCategoryId === id,
          )?.category
        : carRentalStandardCategory,
    [carRentalCategories, carRentalStandardCategory, selectedOptions, step.id],
  );

  const stepWithOnlySelectedCategory = useMemo(
    () =>
      ({
        ...step,
        standardBenefit: {
          ...step.standardBenefit,
          carRental: {
            ...step.standardBenefit?.carRental,
            carCategories: [selectedCarCategory],
          },
        },
      }) as CircuitDayFormData['steps'][number]['transport'],
    [selectedCarCategory, step],
  );

  const mainCategoryImages = useMemo(() => {
    return selectedCarCategory?.images ?? [];
  }, [selectedCarCategory?.images]);

  const mainCategoryImageKey = useMemo(() => {
    return mainCategoryImages.length > 0 ? mainCategoryImages[0] : null;
  }, [mainCategoryImages]);

  const { data: imageUrl } = useGetFileUrl(
    mainCategoryImageKey,
    false,
    accessToken,
  );

  const { carCategoryInformation } = useEstimateCarRental({
    carCategory: selectedCarCategory,
    originalCurrency: step.standardBenefit?.currency,
    currency,
    language,
  });

  const carRentalCategoryOptions = useMemo(() => {
    return selectedOptions[step.id]?.carCategoryOptions ?? {};
  }, [selectedOptions, step.id]);

  const getCarRentalCategoryElement = useCallback(
    (elementType: keyof typeof carRentalCategoryOptions) => {
      const translatedElements =
        language && selectedCarCategory?.translations
          ? selectedCarCategory.translations[language]?.[elementType]
          : undefined;

      return (
        carRentalCategoryOptions[elementType]
          ?.map(({ elementId, deltaPrice, isRequired }) => {
            const element = selectedCarCategory?.[elementType]?.find(
              ({ id }) => id === elementId,
            );
            if (!element) return null;

            const elementIndex = selectedCarCategory?.[elementType]?.findIndex(
              ({ id }) => id === elementId,
            );
            const translatedElement =
              elementIndex !== undefined && elementIndex >= 0
                ? translatedElements?.[elementIndex]
                : undefined;

            return {
              deltaPrice,
              name: translatedElement?.name ?? element.name,
              elementId,
              isRequired,
            };
          })
          .filter((v) => v != null) ?? []
      );
    },
    [carRentalCategoryOptions, selectedCarCategory, language],
  );

  const standardName = useMemo(() => {
    const translatedCategory =
      language && selectedCarCategory?.translations
        ? selectedCarCategory.translations[language]
        : undefined;
    const category =
      (translatedCategory ? translatedCategory.category : undefined) ??
      selectedCarCategory?.category ??
      '';
    const model =
      (translatedCategory ? translatedCategory.model : undefined) ??
      selectedCarCategory?.model ??
      '';
    return `${category} - ${model}`;
  }, [language, selectedCarCategory]);

  return (
    <EstimateDayStepCardTemplate
      step={stepWithOnlySelectedCategory}
      type={BenefitType.VEHICLE}
      stepDates={stepDates}
      standardName={standardName}
      titlePrice={selectedOptions[step.id]?.deltaPrice || undefined}
      displayOptionalBenefits={false}
      setDisplayOptionalBenefits={() => {}}
      generalBenefitInformation={carCategoryInformation}
      imageUrl={imageUrl?.url}
      imagesKeys={mainCategoryImages}
      shouldDisplayDetailsBtn={false}
      shouldDisplayReadMoreBtn={false}
      creationDate={creationDate}
      currency={currency}
      language={language}
      standardBenefitOptions={
        <>
          <EstimateCarRentalElementSummary
            elementType="insurances"
            elements={getCarRentalCategoryElement('insurances')}
            currency={currency}
            creationDate={creationDate}
          />
          <EstimateCarRentalElementSummary
            elementType="options"
            elements={getCarRentalCategoryElement('options')}
            currency={currency}
            creationDate={creationDate}
          />
          <EstimateCarRentalElementSummary
            elementType="taxes"
            elements={getCarRentalCategoryElement('taxes')}
            currency={currency}
            creationDate={creationDate}
          />
          <EstimateCarRentalElementSummary
            elementType="oneWayFees"
            elements={getCarRentalCategoryElement('oneWayFees')}
            currency={currency}
            creationDate={creationDate}
          />
        </>
      }
      accessToken={accessToken}
    />
  );
};

export default EstimateDayStepCarRentalSummaryCard;

interface EstimateCarRentalElementSummaryProps<T extends CarRentalElementType> {
  elementType: T;
  elements: {
    name?: string | null;
    deltaPrice: number;
    elementId: string;
    isRequired?: boolean;
  }[];
  currency?: string;
  creationDate?: Date;
}

const EstimateCarRentalElementSummary = <T extends CarRentalElementType>({
  elementType,
  elements,
  currency = 'EUR',
  creationDate,
}: EstimateCarRentalElementSummaryProps<T>) => {
  const { t } = useTranslation();
  const convertPrice = useConvertPriceOnlyIfDateISProvided(creationDate);

  const title = useMemo(() => {
    switch (elementType) {
      case 'insurances':
        return t('estimates.details.carRentalElements.insurances');
      case 'options':
        return t('estimates.details.carRentalElements.options');
      case 'taxes':
        return t('estimates.details.carRentalElements.taxes');
      case 'oneWayFees':
        return t('estimates.details.carRentalElements.oneWayFees');
    }
  }, [elementType, t]);

  if (elements.length === 0) return null;

  return (
    <div className="flex flex-col text-sm font-normal text-gray-800">
      <h1 className="font-medium">{title}</h1>
      {elements.map((element, elementIndex) => (
        <div
          className="flex flex-row gap-4 items-center"
          key={`estimate-summary-${element.elementId}-${elementIndex}`}
        >
          {element.name && (
            <span>
              {element.name}{' '}
              {element.isRequired &&
                `(${t('estimates.details.carRentalElements.required')})`}
            </span>
          )}
          <span className="font-semibold text-brand-700 whitespace-nowrap">
            + {formatPrice(convertPrice(element.deltaPrice, 'EUR', currency))}{' '}
            {formatCurrency(currency)}
          </span>
        </div>
      ))}
    </div>
  );
};
